import { apiCustomerPublic } from "@/api";
import authSheetAtom from "@/atoms/auth-sheet";
import { requestBirthdayPromptAtom } from "@/atoms/birthday-prompt";
import { profileAtom } from "@/atoms";
import { showToast } from "@/components/feedback";
import { Input, InputMask } from "@/components/forms";
import { BottomSheet } from "@/components/modals/BottomSheet";
import { Button, Callout, Card, CloseButton, IconBadge, SheetBody, SheetHeader, Text } from "@/components/ui";
import { CHROME_Z_INDEX } from "@/contexts/ModalContext";
import { resetCartInitialized } from "@/hooks/use-cart";
import { claimStorefrontSession } from "@/domain/storefront-analytics";
import { guestSessionEpoch, storeGuestSession, ensureGuestToken, currentGuestToken, hasRecoverableGuestSession, guestRecoveryUnavailable, GUEST_SESSION_EVENT } from "@/lib/guest-session";
import { loginSchema, LoginSchemaType } from "@/types/login-schema";
import Profile from "@/types/profile";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Dialog from "@radix-ui/react-dialog";
import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { Phone } from "@/icons";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

/** Displays the isolated guest sign-in sheet. */
export function AuthSheet() {
  const [{ open, redirectTo }, setAuthSheet] = useAtom(authSheetAtom);

  const openTicket = useRef(0);
  const [canRecover, setCanRecover] = useState(hasRecoverableGuestSession);
  const [recoveryUnavailable, setRecoveryUnavailable] = useState(guestRecoveryUnavailable);

  useEffect(() => {
    const update = () => {
      setCanRecover(hasRecoverableGuestSession());
      setRecoveryUnavailable(guestRecoveryUnavailable());
    };
    window.addEventListener(GUEST_SESSION_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(GUEST_SESSION_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  function close() {
    openTicket.current++;
    setAuthSheet({ open: false });
  }

  return (
    <>
      {recoveryUnavailable && !open && (
        <div className="fixed inset-x-3 bottom-4" style={{ zIndex: CHROME_Z_INDEX.bar }}>
          <Card variant="elevated" padding="none">
            <Callout
              tone="info"
              action={<Button onClick={() => setAuthSheet({ open: true })}>Tentar recuperar sessão</Button>}
            >
              Sua sessão está guardada. Não foi possível recuperá-la agora.
            </Callout>
          </Card>
        </div>
      )}
    <BottomSheet
      open={open}
      describedByChildren={!canRecover}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <Dialog.Close asChild>
        <CloseButton className="absolute right-4 top-4" />
      </Dialog.Close>

      {canRecover ? (
        <GuestRecoveryForm key={openTicket.current} redirectTo={redirectTo} onClose={close} currentTicket={() => openTicket.current} />
      ) : (
        <AuthSheetForm key={openTicket.current} redirectTo={redirectTo} onClose={close} currentTicket={() => openTicket.current} />
      )}
    </BottomSheet>
    </>
  );
}

function GuestRecoveryForm({ redirectTo, onClose, currentTicket }: {
  redirectTo?: string;
  onClose: () => void;
  currentTicket: () => number;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(guestRecoveryUnavailable);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  async function retry() {
    const ticket = currentTicket();
    setLoading(true);
    const token = await ensureGuestToken();
    if (!mounted.current || currentTicket() !== ticket) return;
    setLoading(false);
    if (token && currentGuestToken() === token) {
      onClose();
      if (redirectTo) navigate(redirectTo);
      else window.location.reload();
    } else {
      setFailed(true);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <SheetHeader title="Continuar sua sessão" />
      <SheetBody className="flex flex-col gap-4 pb-safe-md">
        <Text tone="muted">
          {failed ? "Não foi possível recuperar sua sessão agora. Sua sessão e seu carrinho foram mantidos." : "Vamos recuperar seu cadastro e continuar de onde você parou."}
        </Text>
        <Button onClick={() => void retry()} disabled={loading} isLoading={loading} fullWidth>
          {loading ? "Aguarde..." : "Tentar novamente"}
        </Button>
      </SheetBody>
    </div>
  );
}

function AuthSheetForm({
  redirectTo,
  onClose,
  currentTicket,
}: {
  redirectTo?: string;
  onClose: () => void;
  currentTicket: () => number;
}) {
  const {
    handleSubmit,
    formState: { errors },
    watch,
    register,
    getValues,
    setValue,
  } = useForm<LoginSchemaType>({ resolver: zodResolver(loginSchema) });

  const navigate = useNavigate();
  const setProfile = useSetAtom(profileAtom);
  const requestBirthdayPrompt = useSetAtom(requestBirthdayPromptAtom);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [formatedPhone, setFormatedPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const autofilledName = useRef("");
  const requestRef = useRef(0);

  const fields = watch();

  const allFieldsFilled = new TextEncoder().encode(fields.name?.trim() ?? "").length >= 3 && fields.phone?.length === 15;

  useEffect(() => {
    setFormatedPhone("");
    if (fields.phone?.length !== 15) return;
    try {
      setFormatedPhone(parsePhoneNumberWithError(fields.phone, "BR").number.slice(1));
    } catch {}
  }, [fields.phone]);

  useEffect(() => {
    if (autofilledName.current && getValues("name") === autofilledName.current) {
      setValue("name", "", { shouldValidate: false });
    }
    autofilledName.current = "";
    if (!formatedPhone) {
      setCheckingName(false);
      return;
    }

    const controller = new AbortController();
    setCheckingName(true);

    void apiCustomerPublic.get<{ name: string }>(`/auth/check/${formatedPhone}`, {
      timeout: 5000,
      signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted || typeof data.name !== "string" || !data.name.trim()) return;
      const currentPhone = parsePhoneNumberWithError(getValues("phone"), "BR").number.slice(1);
      if (currentPhone !== formatedPhone || getValues("name")?.trim()) return;
      autofilledName.current = data.name;
      setValue("name", data.name, { shouldValidate: true });
    }).catch(() => undefined).finally(() => {
      if (!controller.signal.aborted) setCheckingName(false);
    });

    return () => controller.abort();
  }, [formatedPhone, getValues, setValue]);

  useEffect(() => () => { requestRef.current++; }, []);

  const onSubmit: SubmitHandler<LoginSchemaType> = async (data) => {
    if (hasRecoverableGuestSession()) return;
    setLoading(true);
    const request = ++requestRef.current;
    const sessionEpoch = guestSessionEpoch();
    const ticket = currentTicket();

    try {
      const response = await apiCustomerPublic.post("/auth/login", {
        phone: parsePhoneNumberWithError(data.phone, "BR").number.slice(1),
        name: data.name.trim(),
      });

      if (requestRef.current !== request || currentTicket() !== ticket) return;
      const profileData: Profile = storeGuestSession(response.data, sessionEpoch);
      resetCartInitialized();
      setProfile(profileData);
      claimStorefrontSession();

      showToast.success("Pronto, seu pedido pode continuar!");
      onClose();
      if (redirectTo) navigate(redirectTo);
      void requestBirthdayPrompt(redirectTo?.startsWith("/cart") ? "order" : "login");
    } catch (err) {
      if (requestRef.current !== request || currentTicket() !== ticket) return;
      if (axios.isAxiosError(err)) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Não deu pra entrar. Confira seus dados e tente de novo.";
        showToast.error(message);
      } else {
        showToast.error("Algo deu errado por aqui. Tente de novo em instantes.");
      }
    }

    if (requestRef.current === request) setLoading(false);
  };

  return (
    <SheetBody className="mx-auto max-h-sheet w-full max-w-md pb-safe-md pt-2">
      <div className="mb-5 flex justify-center">
        <IconBadge icon={Phone} size="xl" variant="solid" tone="primary" />
      </div>

      <SheetHeader align="center" title="Entrar ou criar conta" description="Informe seu telefone para recuperar seu cadastro e seus endereços salvos." className="px-0 pb-6" />

      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
        <InputMask
          name="phone"
          label="Telefone/WhatsApp"
          error={!!errors.phone}
          errorMsg="Telefone inválido"
          registerAction={register}
          mask="(__) _____-____"
          replacement={{ _: /\d/ }}
          placeholder="(00) 00000-0000"
          disabled={loading}
          autoFocus
          required
        />

        {checkingName && (
          <Text role="status" size="sm" tone="muted">Buscando seu nome...</Text>
        )}

        <Input
          name="name"
          label="Nome e sobrenome"
          error={!!errors.name}
          errorMsg="Informe seu nome"
          registerAction={register}
          placeholder="Digite seu nome completo"
          disabled={loading}
          required
        />

        <Button
          type="submit"
          ref={buttonRef}
          size="lg"
          fullWidth
          disabled={loading || checkingName || !allFieldsFilled || !formatedPhone}
          isLoading={loading}
          className="mt-1"
        >
          {loading ? "Aguarde..." : "Continuar"}
        </Button>
      </form>
    </SheetBody>
  );
}
